import React, { useState } from 'react';
import { FormField, Input, Select } from '../ui';
import { BusinessInfo, LEGAL_ENTITIES, INDUSTRIES } from '../../types';

interface BusinessInfoFormProps {
  data: BusinessInfo;
  onChange: (data: Partial<BusinessInfo>) => void;
  errors: Record<string, string>;
}

const legalEntityOptions = LEGAL_ENTITIES.map((entity) => ({
  value: entity,
  label: entity,
}));

const industryOptions = INDUSTRIES.map((industry) => ({
  value: industry,
  label: industry,
}));

export const BusinessInfoForm: React.FC<BusinessInfoFormProps> = React.memo(({
  data,
  onChange,
  errors,
}) => {
  // Auto-expand the optional block if VAT already has a value or an error
  // so the user isn't confused by a "hidden" field that's being validated.
  const [optionalOpen, setOptionalOpen] = useState(
    Boolean(data.vatNumber || errors.vatNumber),
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <FormField
          label="Company Name"
          required
          error={errors.name}
          htmlFor="company-name"
        >
          <Input
            id="company-name"
            type="text"
            value={data.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="Acme Corporation"
            error={errors.name}
          />
        </FormField>

        <FormField
          label="Legal Entity Type"
          required
          error={errors.legalEntity}
        >
          <Select
            options={legalEntityOptions}
            value={data.legalEntity}
            onChange={(value) => onChange({ legalEntity: value })}
            placeholder="Select entity type"
            error={errors.legalEntity}
          />
        </FormField>

        <FormField
          label="Registration Number"
          required
          error={errors.registrationNumber}
          htmlFor="registration-number"
          hint="Your company registration or incorporation number"
        >
          <Input
            id="registration-number"
            type="text"
            value={data.registrationNumber}
            onChange={(e) => onChange({ registrationNumber: e.target.value })}
            placeholder="HRB 123456"
            error={errors.registrationNumber}
          />
        </FormField>

        <FormField
          label="Business Type"
          required
          error={errors.businessType}
          htmlFor="business-type"
        >
          <Input
            id="business-type"
            type="text"
            value={data.businessType}
            onChange={(e) => onChange({ businessType: e.target.value })}
            placeholder="Software Development"
            error={errors.businessType}
          />
        </FormField>

        <FormField
          label="Industry"
          required
          error={errors.industry}
        >
          <Select
            options={industryOptions}
            value={data.industry}
            onChange={(value) => onChange({ industry: value })}
            placeholder="Select industry"
            searchable
            error={errors.industry}
          />
        </FormField>
      </div>

      {/* Optional additional fields — collapsed by default to keep the
          primary form scan short. Auto-opens if there's a value or
          validation error inside. */}
      <div
        style={{
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-sunk)',
          overflow: 'hidden',
        }}
      >
        <button
          type="button"
          onClick={() => setOptionalOpen((v) => !v)}
          aria-expanded={optionalOpen}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-4)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--ink)',
            textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <span className="eyebrow">Optional</span>
            <span style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>
              VAT number
            </span>
          </div>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: optionalOpen ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform var(--transition-fast)',
              color: 'var(--ink-muted)',
            }}
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
        {optionalOpen && (
          <div
            style={{
              padding: 'var(--space-4)',
              background: 'var(--bg-elevated)',
              borderTop: '1px solid var(--line)',
            }}
          >
            <FormField
              label="VAT Number"
              error={errors.vatNumber}
              htmlFor="vat-number"
              hint="Optional — required in some regions (EU, UK, UAE) for invoicing"
            >
              <Input
                id="vat-number"
                type="text"
                value={data.vatNumber || ''}
                onChange={(e) => onChange({ vatNumber: e.target.value })}
                placeholder="DE123456789"
                error={errors.vatNumber}
              />
            </FormField>
          </div>
        )}
      </div>
    </div>
  );
});

BusinessInfoForm.displayName = 'BusinessInfoForm';

export default BusinessInfoForm;
